import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { readFreeWorkoutGroups } from "@/lib/fitness/freeWorkoutSelection";
import { motion, useReducedMotion } from "framer-motion";
import MobileLayout from "@/components/MobileLayout";
import { ChevronLeft, Dumbbell } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { MUSCLE_GROUP_MAP } from "@/lib/fitnessMuscleGroups";
import { MUSCLE_GROUP_LIST, MuscleGroupKey } from "@/lib/exerciseLibrary";

const PARTICLE_CONFIGS = [
  { w: 2, color: "#00e5ff", opacity: 0.17, left: "12%", top: "18%", yDrift: -15, xDrift: 6, dur: 10, delay: 0 },
  { w: 1.5, color: "hsl(265 60% 65%)", opacity: 0.15, left: "78%", top: "30%", yDrift: -12, xDrift: -5, dur: 12, delay: 1.5 },
  { w: 2.5, color: "#00e5ff", opacity: 0.18, left: "45%", top: "55%", yDrift: -18, xDrift: 4, dur: 9, delay: 0.8 },
  { w: 1.8, color: "hsl(265 60% 65%)", opacity: 0.16, left: "28%", top: "72%", yDrift: -14, xDrift: -7, dur: 11, delay: 2.5 },
  { w: 2, color: "#00e5ff", opacity: 0.19, left: "65%", top: "42%", yDrift: -16, xDrift: 5, dur: 13, delay: 1 },
  { w: 1.6, color: "hsl(265 60% 65%)", opacity: 0.15, left: "88%", top: "65%", yDrift: -13, xDrift: -4, dur: 11.5, delay: 2 },
];

const FitnessParticles = React.memo(() => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
    {PARTICLE_CONFIGS.map((p, i) => (
      <motion.div key={i} className="absolute rounded-full"
        style={{ width: p.w, height: p.w, background: p.color, opacity: p.opacity, left: p.left, top: p.top }}
        animate={{ y: [0, p.yDrift, 0], x: [0, p.xDrift, 0], opacity: [p.opacity, p.opacity + 0.04, p.opacity] }}
        transition={{ duration: p.dur, repeat: Infinity, ease: "easeInOut", delay: p.delay }} />
    ))}
  </div>
));
FitnessParticles.displayName = "FitnessParticles";

const FitnessBreathingOrbs = React.memo(() => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
    <motion.div
      className="absolute -top-20 -right-20 w-[280px] h-[280px] rounded-full"
      style={{ background: "radial-gradient(circle, rgba(0,229,255,0.04) 0%, transparent 70%)" }}
      animate={{ opacity: [0.3, 0.6, 0.3], scale: [0.95, 1.05, 0.95] }}
      transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute -bottom-32 -left-24 w-[250px] h-[250px] rounded-full"
      style={{ background: "radial-gradient(circle, rgba(139,92,246,0.03) 0%, transparent 70%)" }}
      animate={{ opacity: [0.2, 0.5, 0.2], scale: [1, 1.08, 1] }}
      transition={{ duration: 10, delay: 2, repeat: Infinity, ease: "easeInOut" }}
    />
  </div>
));
FitnessBreathingOrbs.displayName = "FitnessBreathingOrbs";

const GROUP_LABEL_KEY: Record<MuscleGroupKey, string> = {
  chest: MUSCLE_GROUP_MAP.chest,
  back: MUSCLE_GROUP_MAP.back,
  shoulders: MUSCLE_GROUP_MAP.shoulders,
  biceps: "fitness.muscles.biceps",
  triceps: "fitness.muscles.triceps",
  legs: MUSCLE_GROUP_MAP.legs,
  core: MUSCLE_GROUP_MAP.core,
  glutes: MUSCLE_GROUP_MAP.glutes,
  cardio: MUSCLE_GROUP_MAP.cardio,
};

const FreeWorkoutGroupSelect: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const reducedMotion = useReducedMotion();
  const [selected, setSelected] = useState<Set<MuscleGroupKey>>(() => new Set(readFreeWorkoutGroups()));

  const toggle = (key: MuscleGroupKey) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleContinue = () => {
    if (selected.size === 0) return;
    const selectedGroups = MUSCLE_GROUP_LIST.filter(g => selected.has(g));
    try {
      sessionStorage.setItem("vyv_free_workout_groups", JSON.stringify(selectedGroups));
    } catch {
      // Route state still carries this setup when browser storage is unavailable.
    }
    navigate("/fitness/free-workout/exercises", { state: { selectedGroups } });
  };

  return (
    <MobileLayout className="pb-28 pt-8 relative overflow-hidden">
      {!reducedMotion && <><FitnessParticles /><FitnessBreathingOrbs /></>}

      <button onClick={() => navigate("/fitness")} className="flex items-center gap-1 text-muted-foreground mb-4 active:opacity-70 relative z-10">
        <ChevronLeft size={16} />
        <span className="text-[13px] font-display">{t("common.back")}</span>
      </button>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-6 relative z-10">
        <div>
          <p className="text-[9px] font-display tracking-[0.2em] uppercase mb-0.5" style={{ color: "hsl(187 72% 50% / 0.55)" }}>
            {t("fitness.freeWorkout.title").toUpperCase()}
          </p>
          <h1 className="font-display text-[26px] font-semibold text-foreground leading-none">{t("fitness.freeWorkout.title")}</h1>
          <p className="text-[12px] font-body text-foreground/55 mt-2">{t("fitness.freeWorkout.subtitle")}</p>
        </div>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "hsl(187 72% 50% / 0.10)", border: "1px solid hsl(187 72% 50% / 0.20)" }}>
          <Dumbbell size={16} style={{ color: "hsl(187 72% 50%)" }} />
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.5 }}
        className="grid grid-cols-2 gap-3 relative z-10"
      >
        {MUSCLE_GROUP_LIST.map((group, idx) => {
          const isSelected = selected.has(group);
          return (
            <motion.button
              key={group}
              type="button"
              aria-pressed={isSelected}
              onClick={() => toggle(group)}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12 + idx * 0.04, duration: 0.4 }}
              className="flex items-center justify-center rounded-2xl active:scale-[0.97] transition-all"
              style={{
                minHeight: 80,
                background: isSelected ? "hsl(187 72% 50% / 0.12)" : "hsl(var(--card))",
                border: isSelected ? "1.5px solid hsl(187 72% 50%)" : "1px solid hsl(222 18% 22%)",
                boxShadow: isSelected ? "0 0 18px hsl(187 72% 50% / 0.22)" : "none",
              }}
            >
              <span
                className="font-display text-[14px] font-semibold text-center"
                style={{ color: isSelected ? "hsl(187 72% 65%)" : "hsl(210 20% 92%)" }}
              >
                {t(GROUP_LABEL_KEY[group])}
              </span>
            </motion.button>
          );
        })}
      </motion.div>

      {/* Fixed continue button */}
      <div className="fixed bottom-20 left-0 right-0 px-6 z-20 pointer-events-none">
        <div className="max-w-[500px] mx-auto pointer-events-auto">
          <button
            onClick={handleContinue}
            disabled={selected.size === 0}
            className="w-full py-4 rounded-xl font-display text-[14px] font-semibold tracking-wide active:scale-[0.97] transition-all"
            style={{
              background: selected.size === 0 ? "hsl(222 18% 14%)" : "hsl(var(--primary))",
              color: selected.size === 0 ? "hsl(218 14% 45%)" : "hsl(var(--background))",
              opacity: selected.size === 0 ? 0.55 : 1,
              boxShadow: selected.size === 0 ? "none" : "0 0 22px hsl(187 72% 50% / 0.28)",
            }}
          >
            {t("fitness.freeWorkout.continueBtn")}
            {selected.size > 0 && ` · ${selected.size}`}
          </button>
        </div>
      </div>

    </MobileLayout>
  );
};

export default FreeWorkoutGroupSelect;
